import { useState } from "react";
import { ChevronDown, ChevronRight, Navigation } from "lucide-react";

const KpiTrends = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const operationalMetrics = [
    {
      id: 1,
      title: "Distance Traveled (per day)",
      formula: "Daily Distance = Σ(distanceTravelled) for voyages departing that day",
      calculation: "Sum of distanceTravelled from VesselVoyage collection where departTime is on target date",
      calculationDetails: [
        "Data Source: VesselVoyage collection → distanceTravelled field",
        "Aggregation: Daily sum across all fleet voyages",
        "Grouping: Grouped by date using $dateToString: '%Y-%m-%d'",
        "Zero Values: Dates with no voyages return 0 distance",
        "Units: Nautical miles (nm) per day"
      ],
      purpose: "Total fleet movement measurement for voyage planning and efficiency analysis",
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: 2,
      title: "Average Speed (per day)",
      formula: "Avg Speed = AVG(avgSpeed) with data validation",
      calculation: "Mean of avgSpeed values excluding null, zero, Infinity, and negative values",
      calculationDetails: [
        "Data Source: VesselVoyage collection → avgSpeed field",
        "Data Cleaning: Filters out invalid values before averaging",
        "Conditions: avgSpeed ≠ null AND > 0 AND ≠ Infinity AND ≠ -Infinity",
        "Aggregation: Daily average using MongoDB $avg aggregation",
        "Units: Knots (kts) per day"
      ],
      purpose: "Fleet speed performance analysis and voyage optimization",
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: 3,
      title: "Utilization Rate (per day)",
      formula: "Utilization = (activeVessels ÷ totalVessels) × 100",
      calculation: "Percentage of vessels with distanceTravelled > 0 vs total fleet vessels",
      calculationDetails: [
        "Active Vessels: Count of vessels with distanceTravelled > 0 on given day",
        "Total Vessels: Total number of vessels in selected fleet(s)",
        "Calculation: (activeVessels / vesselCount) × 100",
        "Condition: Uses $cond to count only vessels with positive distance",
        "Units: Percentage (%) per day"
      ],
      purpose: "Fleet capacity utilization measurement and asset management",
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#b6faed] rounded-lg">
              <Navigation className="w-6 h-6 text-[#117864]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                KPI Trends - Fleet Operations
              </h2>
              <p className="text-gray-600 text-sm">
                Daily fleet movement, speed, and utilization calculations from VesselVoyage data
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded">
              <p className="text-sm text-teal-800">
                <strong>Data Source:</strong> All KPIs calculated from <strong>VesselVoyage collection</strong> using MongoDB aggregation. 
                Single query processes distance, speed, and utilization metrics with data validation and complete time series.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {operationalMetrics.map((metric) => (
                <div
                  key={metric.id}
                  className={`p-5 rounded-lg border ${metric.borderColor} ${metric.bgColor}`}
                >
                  <h4 className="font-semibold text-gray-800 mb-3">
                    {metric.title}
                  </h4>
                  
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                    <div className="p-2 bg-white rounded border border-gray-200">
                      <p className={`font-mono text-sm ${metric.textColor}`}>
                        {metric.formula}
                      </p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Calculation Logic:</p>
                    <p className={`text-sm ${metric.textColor} italic`}>
                      {metric.calculation}
                    </p>
                  </div>

                  {metric.calculationDetails && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Detailed Implementation:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {metric.calculationDetails.map((detail, index) => (
                          <li key={index}>• {detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-1">Purpose:</p>
                    <p className={`text-sm ${metric.textColor} font-medium`}>
                      {metric.purpose}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KpiTrends;